import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    Avatar,
    Button,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
    Snackbar,
    Divider,
    Checkbox,
    Switch,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InfoIcon from '@mui/icons-material/Info';
import { GET_HR_CONTACTS, UPDATE_HR_CONTACT } from '../../api';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../common/tableStyles';
import { AuthContext } from '../../context/logeduser';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: '22px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
    textTransform: 'none', fontWeight: 700, borderRadius: '12px',
    background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
    '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

// Soft tinted status pill sx (matches the rest of the portal).
const chipSx = (fg, bg) => ({ fontWeight: 700, bgcolor: bg, color: fg, border: 'none' });

const MODULES = [
    { id: 'dashboard', label: 'Dashboard', description: 'Overview of system metrics' },
    { id: 'employees', label: 'Employee Management', description: 'Add, edit, and view employee profiles' },
    { id: 'leaves', label: 'Leave Requests', description: 'Approve, reject, and view leave history' },
    { id: 'recruitment', label: 'Recruitment (Candidates)', description: 'Manage candidate pipeline and interviews' },
    { id: 'payroll', label: 'Payroll Management', description: 'Process salaries, bulk updates, and payslips' },
    { id: 'master_records', label: 'Master Records', description: 'Manage branches, locations, and managers' },
    { id: 'settings', label: 'Global Settings', description: 'System configuration and roles' },
];

const RoleManagement = () => {
    const { authData } = useContext(AuthContext);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedHR, setSelectedHR] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const data = await GET_HR_CONTACTS();
            setContacts(data);
        } catch (error) {
            console.error('Error fetching HR contacts:', error);
            showNotify('Failed to load HR contacts', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showNotify = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSelectHR = (hr) => {
        // Initialize permissions if they don't exist
        const initialPermissions = hr.permissions || {};
        MODULES.forEach(module => {
            if (!initialPermissions[module.id]) {
                initialPermissions[module.id] = { view: true, edit: hr.is_restricted ? false : true };
            }
        });
        setSelectedHR({ ...hr, permissions: initialPermissions });
    };

    const handleToggleAccessType = () => {
        const newIsRestricted = !selectedHR.is_restricted;
        
        // Default permissions based on access type
        const newPermissions = { ...selectedHR.permissions };
        MODULES.forEach(module => {
            newPermissions[module.id] = {
                view: true,
                edit: !newIsRestricted
            };
        });

        setSelectedHR({
            ...selectedHR,
            is_restricted: newIsRestricted,
            permissions: newPermissions
        });
    };

    const handlePermissionChange = (moduleId, action) => {
        setSelectedHR(prev => {
            const currentModule = prev.permissions[moduleId] || { view: false, edit: false };
            return {
                ...prev,
                permissions: {
                    ...prev.permissions,
                    [moduleId]: {
                        ...currentModule,
                        [action]: !currentModule[action]
                    }
                }
            };
        });
    };

    const handleSave = async () => {
        if (!selectedHR) return;
        
        // Self-restriction protection
        if (selectedHR.is_restricted && selectedHR.email === authData?.admin?.email) {
            showNotify('You cannot restrict your own access levels.', 'warning');
            return;
        }

        setSaving(true);
        try {
            await UPDATE_HR_CONTACT(selectedHR.id, selectedHR);
            showNotify('Access permissions updated and saved successfully.');
            
            // Refresh local list
            setContacts(prev => prev.map(c => c.id === selectedHR.id ? selectedHR : c));
            
            // If the user updated themselves, we might need a refresh or context update
        } catch (error) {
            console.error('Error saving role:', error);
            showNotify('Failed to save permissions. Check backend connection.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const filteredContacts = contacts.filter(c => 
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        c.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress color="primary" />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Header card */}
            <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <AdminPanelSettingsIcon />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                            User Role & Access Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Search for HR staff to configure their credentials and module-specific permissions.
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Grid container spacing={3}>
                {/* Search & Select HR */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #eef0f4' }}>
                            <TextField
                                fullWidth
                                placeholder="Search HR name or email..."
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#9CA3AF' }} />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: '12px' }
                                }}
                            />
                        </Box>
                        <List sx={{ maxHeight: '600px', overflowY: 'auto', p: 0 }}>
                            {filteredContacts.length === 0 ? (
                                <Box sx={{ p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                                    <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <PersonSearchIcon />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>No contacts found.</Typography>
                                </Box>
                            ) : (
                                filteredContacts.map((hr) => (
                                    <ListItem
                                        key={hr.id}
                                        button
                                        selected={selectedHR?.id === hr.id}
                                        onClick={() => handleSelectHR(hr)}
                                        sx={{
                                            py: 2,
                                            borderLeft: selectedHR?.id === hr.id ? `4px solid ${ACCENT}` : '4px solid transparent',
                                            '&.Mui-selected': { bgcolor: '#eff6ff' }
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar src={hr.photo} sx={{ bgcolor: 'primary.light' }}>
                                                {hr.name.charAt(0)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={<Typography sx={{ fontWeight: 700 }}>{hr.name}</Typography>}
                                            secondary={hr.email}
                                        />
                                        {selectedHR?.id === hr.id && <ArrowForwardIosIcon sx={{ fontSize: 14, color: ACCENT }} />}
                                    </ListItem>
                                ))
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Configuration Workspace */}
                <Grid item xs={12} md={8}>
                    {selectedHR ? (
                        <Box>
                            <Card elevation={0} sx={{ ...CARD_SX, p: { xs: 2.5, md: 4 }, mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                                    <Avatar
                                        src={selectedHR.photo}
                                        sx={{ width: 80, height: 80, mr: 3, boxShadow: '0 6px 24px rgba(17,24,39,0.05)' }}
                                    >
                                        {selectedHR.name.charAt(0)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827' }}>{selectedHR.name}</Typography>
                                        <Typography sx={{ mb: 1, color: '#6b7280' }}>{selectedHR.email}</Typography>
                                        <Stack direction="row" spacing={1}>
                                            <Chip
                                                icon={selectedHR.is_restricted ? <LockPersonIcon /> : <AdminPanelSettingsIcon />}
                                                label={selectedHR.is_restricted ? "Restricted Access" : "Full Access Admin"}
                                                sx={selectedHR.is_restricted ? chipSx('#f59e0b', '#fffbeb') : chipSx('#16a34a', '#f0fdf4')}
                                            />
                                            {(selectedHR.branches?.length
                                                ? selectedHR.branches
                                                : (selectedHR.branch ? [selectedHR.branch] : ['No Branch'])
                                            ).map((branch) => (
                                                <Chip
                                                    key={branch}
                                                    label={branch}
                                                    size="small"
                                                    sx={chipSx(ACCENT, '#eef4ff')}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                    <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                                        <Typography variant="caption" display="block" sx={{ color: '#6b7280', fontWeight: 600 }}>Access Type Toggle</Typography>
                                        <Switch
                                            checked={!selectedHR.is_restricted}
                                            onChange={handleToggleAccessType}
                                            color="primary"
                                        />
                                    </Box>
                                </Box>

                                <Divider sx={{ mb: 4 }} />

                                <Box sx={{ mb: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                                        <Box sx={{ width: 36, height: 36, borderRadius: '11px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <SecurityIcon sx={{ fontSize: 20 }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>Module Permissions</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
                                        Define granular permissions for each system module. For Restricted HR, you can enable View-only access or allow Editing.
                                    </Typography>

                                    <TableContainer component={Paper} elevation={0} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)' }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ ...HEAD_CELL_SX }}>System Module</TableCell>
                                                    <TableCell align="center" sx={{ ...HEAD_CELL_SX }}>View Access</TableCell>
                                                    <TableCell align="center" sx={{ ...HEAD_CELL_SX }}>Edit Access</TableCell>
                                                    <TableCell sx={{ ...HEAD_CELL_SX }}>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {MODULES.map((module) => (
                                                    <TableRow key={module.id} sx={{ ...TABLE_ROW_SX }}>
                                                        <TableCell sx={{ ...BODY_CELL_SX }}>
                                                            <Typography sx={{ fontWeight: 600 }}>{module.label}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {module.description}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...BODY_CELL_SX }}>
                                                            <Checkbox
                                                                checked={selectedHR.permissions[module.id]?.view || false}
                                                                onChange={() => handlePermissionChange(module.id, 'view')}
                                                                disabled={!selectedHR.is_restricted} // Admins always have view
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ ...BODY_CELL_SX }}>
                                                            <Checkbox
                                                                checked={selectedHR.permissions[module.id]?.edit || false}
                                                                onChange={() => handlePermissionChange(module.id, 'edit')}
                                                                disabled={!selectedHR.is_restricted} // Admins always have edit
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ ...BODY_CELL_SX }}>
                                                            {selectedHR.permissions[module.id]?.edit ? (
                                                                <Chip label="Full Access" size="small" sx={chipSx('#16a34a', '#f0fdf4')} />
                                                            ) : selectedHR.permissions[module.id]?.view ? (
                                                                <Chip label="View Only" size="small" sx={chipSx(ACCENT, '#eef4ff')} />
                                                            ) : (
                                                                <Chip label="No Access" size="small" sx={chipSx('#ef4444', '#fef2f2')} />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setSelectedHR(null)}
                                        sx={OUTLINED_BTN}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CloudDoneIcon />}
                                        onClick={handleSave}
                                        disabled={saving}
                                        sx={{ ...GRADIENT_BTN, px: 4 }}
                                    >
                                        {saving ? 'Saving...' : 'Save & Submit Permissions'}
                                    </Button>
                                </Box>
                            </Card>

                            <Alert severity="info" sx={{ borderRadius: '16px' }} icon={<InfoIcon />}>
                                <strong>Tip:</strong> Users with "Full Access Admin" role have all permissions enabled by default. Use "Restricted Access" to customize specific module visibility.
                            </Alert>
                        </Box>
                    ) : (
                        <Paper
                            elevation={0}
                            sx={{
                                ...CARD_SX,
                                height: '600px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            <Box sx={{ width: 64, height: 64, borderRadius: '18px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PersonSearchIcon sx={{ fontSize: 34 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#6b7280', textAlign: 'center', px: 2 }}>
                                Select an HR contact from the list to manage their roles
                            </Typography>
                        </Paper>
                    )}
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%', borderRadius: '12px', boxShadow: 3 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default RoleManagement;
